// About 画面の P2P ストリーミング解説アニメーション。
// PIN コード → ホスト 3 ウィンドウ + システムサウンド → 直接接続線 → 視聴側
// (PC + モバイル) で映像が灯る、というストーリーを 4~5 秒のループで提示する。
//
// 論理キャンバスは 800x420。実 canvas (CSS で aspect-ratio: 800/420) のサイズに
// 合わせて全要素を均等スケール + 中央配置するため、モバイルでも比率が崩れない。
// 描画ループは getBoundingClientRect の 0 サイズ判定で join 画面が hidden に
// なっている間はスキップする (join-bg.ts と同じパターン)。

// hero-anim 内のメインループには時間変数 `t` がある (秒経過) ため、
// i18n の `t()` は別名で取り込んで衝突を避ける。
import { getLocale, t as tr } from "./i18n.js";

// canvas 内で日本語キャプションを描画するときに使うフォント。monospace では
// 日本語が組まれず代替フォントに置き換わって不格好になるので、ロケールに応じて
// 切り替える。PIN 数字は常に半角英数なので別途 monospace を直書き。
function labelFont(px: number): string {
  return getLocale() === "ja"
    ? `400 ${px}px -apple-system, BlinkMacSystemFont, "Hiragino Kaku Gothic ProN", "Hiragino Sans", "Yu Gothic", Meiryo, sans-serif`
    : `400 ${px}px ui-monospace, SFMono-Regular, Menlo, monospace`;
}

type Dot = { x: number; y: number; a: number; ph: number };
type Vec2 = { x: number; y: number };
type Curve = [Vec2, Vec2, Vec2];

const LW = 800;
const LH = 420;

const BG = "#0d1117";
const PANEL = "#161b22";
const BORDER = "#30363d";
const BLUE = "#58a6ff";
const GREEN = "#3fb950";
const TXT = "#8b949e";

const winCols: readonly string[] = [BLUE, GREEN, "#d29922"];

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
function eoc(v: number): number {
  const c = clamp01(v);
  return 1 - Math.pow(1 - c, 3);
}
function eob(v: number): number {
  const c = clamp01(v);
  const a = 1.70158;
  const b = a + 1;
  return 1 + b * Math.pow(c - 1, 3) + a * Math.pow(c - 1, 2);
}

function rr(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function qp(p0: Vec2, p1: Vec2, p2: Vec2, u: number): Vec2 {
  const a = (1 - u) * (1 - u);
  const b = 2 * (1 - u) * u;
  const c = u * u;
  return { x: a * p0.x + b * p1.x + c * p2.x, y: a * p0.y + b * p1.y + c * p2.y };
}

function windowCard(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  rot: number,
  col: string,
  ph: number,
  t: number,
  lit: number
): void {
  ctx.save();
  ctx.translate(x + w / 2, y + h / 2);
  ctx.rotate(rot);
  ctx.translate(-w / 2, -h / 2);
  // ホストのウィンドウカードも viewer 側ミニウィンドウと意匠を揃え色付き枠線にする。
  ctx.fillStyle = PANEL;
  ctx.strokeStyle = col;
  ctx.lineWidth = 1;
  rr(ctx, 0, 0, w, h, 6);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = col;
  ctx.beginPath();
  ctx.arc(12, 11, 3.5, 0, 6.28);
  ctx.fill();
  ctx.fillStyle = "#21262d";
  rr(ctx, 22, 8, w * 0.45, 6, 3);
  ctx.fill();
  ctx.strokeStyle = col;
  ctx.beginPath();
  ctx.moveTo(0, 21);
  ctx.lineTo(w, 21);
  ctx.stroke();
  // バー描画では外側 globalAlpha (eob による出現フェード) を保持して内部の
  // 0.85/1 と乗算する。直接 0.85 を代入するとフェード中もバーだけ常時不透明で
  // 描画され「バーが先に出る」見え方になるため。
  const outerA = ctx.globalAlpha;
  for (let bi = 0; bi < 3; bi++) {
    const bw = (0.55 + 0.35 * Math.sin(t * 0.9 + ph + bi * 1.9)) * (w - 24);
    ctx.fillStyle = bi === 0 ? col : "#21262d";
    ctx.globalAlpha = (bi === 0 ? 0.85 : 1) * outerA;
    rr(ctx, 12, 30 + bi * 13, Math.max(14, bw * (bi === 0 ? 0.5 : 1)), 7, 3.5);
    ctx.fill();
  }
  ctx.globalAlpha = outerA;
  if (lit > 0) {
    ctx.globalAlpha = 0.1 * lit * outerA;
    ctx.fillStyle = col;
    rr(ctx, 0, 0, w, h, 6);
    ctx.fill();
    ctx.globalAlpha = outerA;
  }
  ctx.restore();
}

export function initHeroAnim(
  canvas: HTMLCanvasElement,
  replayButton: HTMLButtonElement | null
): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  let W = 0;
  let H = 0;

  const resize = (): void => {
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    W = rect.width;
    H = rect.height;
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  resize();
  window.addEventListener("resize", resize);

  const dots: Dot[] = [];
  for (let i = 0; i < 70; i++) {
    dots.push({
      x: Math.random(),
      y: Math.random(),
      a: 0.04 + Math.random() * 0.08,
      ph: Math.random() * 6.28,
    });
  }

  let start = performance.now();
  if (replayButton) {
    replayButton.addEventListener("click", () => {
      start = performance.now();
    });
  }

  const frame = (now: number): void => {
    const rect = canvas.getBoundingClientRect();
    // join 画面が hidden の間は計算スキップ。アニメ自体はそのまま rAF を回し
    // 再表示時に即再開できるようにする。
    if (rect.width === 0 || rect.height === 0) {
      requestAnimationFrame(frame);
      return;
    }
    if (Math.abs(rect.width - W) > 0.5 || Math.abs(rect.height - H) > 0.5) {
      resize();
    }

    const t = (now - start) / 1000;
    const amb = now / 1000;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, W, H);

    // 論理キャンバス 800x420 を基準に書かれた以下の描画を、実 canvas に合わせ
    // 均等スケール + 中央配置。
    const SCALE = Math.min(1, W / LW, H / LH);
    const OX = (W - LW * SCALE) / 2;
    const OY = (H - LH * SCALE) / 2;
    ctx.save();
    ctx.translate(OX, OY);
    ctx.scale(SCALE, SCALE);

    for (const d of dots) {
      ctx.fillStyle =
        "rgba(88,166,255," +
        (d.a * (0.6 + 0.4 * Math.sin(amb * 0.7 + d.ph))).toFixed(3) +
        ")";
      ctx.beginPath();
      ctx.arc(d.x * LW, d.y * LH, 1, 0, 6.28);
      ctx.fill();
    }

    // PIN コード/タグは画面上端に最後まで残す (フェードアウトしない)。
    const pinY = 46;
    const pinCX = LW * 0.5;
    const digits = ["4", "7", "2", "9"] as const;
    const bw = 34;
    const gap = 9;
    const totalW = digits.length * bw + (digits.length - 1) * gap;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (let pi = 0; pi < 4; pi++) {
      const p = eob((t - 0.2 - pi * 0.16) / 0.5);
      if (p <= 0) continue;
      const ch = digits[pi];
      if (!ch) continue;
      const x = pinCX - totalW / 2 + pi * (bw + gap);
      ctx.save();
      ctx.translate(x + bw / 2, pinY);
      ctx.scale(p, p);
      ctx.globalAlpha = clamp01(p);
      ctx.fillStyle = PANEL;
      ctx.strokeStyle = BORDER;
      ctx.lineWidth = 1;
      rr(ctx, -bw / 2, -22, bw, 44, 8);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = BLUE;
      ctx.font = "500 21px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.fillText(ch, 0, 1);
      ctx.restore();
    }
    const tagA = eoc((t - 1.0) / 0.6);
    if (tagA > 0) {
      ctx.globalAlpha = tagA;
      ctx.fillStyle = TXT;
      ctx.font = labelFont(12);
      ctx.fillText(tr("anim.tagline"), pinCX, pinY + 38);
      ctx.globalAlpha = 1;
    }

    const hostX = LW * 0.22;
    const hostY = LH * 0.56;
    const lit = eoc((t - 3.6) / 0.8);
    const offs: ReadonlyArray<{ dx: number; dy: number; r: number }> = [
      { dx: -34, dy: -34, r: -0.1 },
      { dx: 30, dy: 34, r: 0.09 },
      { dx: 0, dy: 0, r: 0 },
    ];
    for (let wi = 0; wi < 3; wi++) {
      const pp = eob((t - 1.9 - wi * 0.18) / 0.6);
      if (pp <= 0) continue;
      const o = offs[wi];
      const col = winCols[wi];
      if (!o || !col) continue;
      const cw = 124;
      const ch = 84;
      const fl = Math.sin(amb * 0.8 + wi * 2.1) * 3;
      ctx.save();
      ctx.globalAlpha = clamp01(pp * 1.4);
      windowCard(
        ctx,
        hostX - cw / 2 + o.dx * pp,
        hostY - ch / 2 + o.dy * pp + fl,
        cw,
        ch,
        o.r * pp,
        col,
        wi * 2.3,
        amb,
        lit
      );
      ctx.restore();
    }

    const audA = eoc((t - 2.8) / 0.6);
    if (audA > 0) {
      // 緑のホストウィンドウ下端 ~hostY+76 とクリアランスを取って配置。
      const ax = hostX - 26;
      const ay = hostY + 92;
      ctx.globalAlpha = audA;
      for (let ai = 0; ai < 5; ai++) {
        const hh = 5 + (lit > 0.5 ? Math.abs(Math.sin(amb * 4 + ai * 1.3)) * 14 : 2);
        ctx.fillStyle = GREEN;
        rr(ctx, ax + ai * 9, ay - hh / 2, 5, hh, 2.5);
        ctx.fill();
      }
      ctx.fillStyle = TXT;
      ctx.font = labelFont(11);
      ctx.textAlign = "left";
      ctx.fillText(tr("anim.systemAudio"), ax + 52, ay);
      ctx.textAlign = "center";
      ctx.globalAlpha = 1;
    }

    const lapX = LW * 0.76;
    const lapY = LH * 0.3;
    const phX = LW * 0.79;
    const phY = LH * 0.7;
    const p0: Vec2 = { x: hostX + 78, y: hostY - 10 };
    const c1: Vec2 = { x: LW * 0.5, y: hostY - 80 };
    const e1: Vec2 = { x: lapX - 72, y: lapY + 10 };
    const c2: Vec2 = { x: LW * 0.52, y: hostY + 70 };
    const e2: Vec2 = { x: phX - 42, y: phY };
    const curves: ReadonlyArray<Curve> = [
      [p0, c1, e1],
      [p0, c2, e2],
    ];

    const draw = eoc((t - 3.0) / 0.9);
    if (draw > 0) {
      ctx.lineWidth = 1.5;
      for (let c = 0; c < curves.length; c++) {
        const cv = curves[c];
        if (!cv) continue;
        ctx.strokeStyle = "rgba(88,166,255,0.35)";
        ctx.beginPath();
        const steps = 40;
        for (let s = 0; s <= steps * draw; s++) {
          const pt = qp(cv[0], cv[1], cv[2], s / steps);
          if (s === 0) ctx.moveTo(pt.x, pt.y);
          else ctx.lineTo(pt.x, pt.y);
        }
        ctx.stroke();
      }
      if (draw > 0.5) {
        ctx.globalAlpha = clamp01((draw - 0.5) * 2);
        ctx.fillStyle = TXT;
        ctx.font = labelFont(11);
        ctx.fillText(tr("anim.directStream"), LW * 0.5, hostY - 64);
        ctx.globalAlpha = 1;
      }
    }

    if (lit > 0) {
      for (let ci = 0; ci < curves.length; ci++) {
        const cv = curves[ci];
        if (!cv) continue;
        for (let k = 0; k < 9; k++) {
          const u = ((amb * (0.22 + ci * 0.04) + k / 9) % 1 + 1) % 1;
          const pt = qp(cv[0], cv[1], cv[2], u);
          const fade = Math.sin(u * Math.PI);
          ctx.fillStyle = "rgba(88,166,255," + (0.9 * fade * lit).toFixed(3) + ")";
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, 2.2, 0, 6.28);
          ctx.fill();
          ctx.fillStyle = "rgba(88,166,255," + (0.25 * fade * lit).toFixed(3) + ")";
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, 4.5, 0, 6.28);
          ctx.fill();
        }
      }
    }

    const vA = eoc((t - 3.4) / 0.7);
    if (vA > 0) {
      ctx.save();
      ctx.globalAlpha = vA;
      const lw = 148;
      const lh = 92;
      ctx.translate(0, (1 - vA) * 14);
      ctx.fillStyle = PANEL;
      ctx.strokeStyle = BORDER;
      ctx.lineWidth = 1;
      rr(ctx, lapX - lw / 2, lapY - lh / 2, lw, lh, 7);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "#21262d";
      rr(ctx, lapX - lw / 2 - 12, lapY + lh / 2, lw + 24, 7, 3.5);
      ctx.fill();
      ctx.fillStyle = BG;
      rr(ctx, lapX - lw / 2 + 6, lapY - lh / 2 + 6, lw - 12, lh - 12, 4);
      ctx.fill();
      if (lit > 0) {
        for (let mi = 0; mi < 3; mi++) {
          const col = winCols[mi];
          if (!col) continue;
          const mw = 38;
          const mh = 26;
          const mx = lapX - lw / 2 + 12 + mi * 42;
          const my = lapY - 8 + (mi === 1 ? 14 : 0);
          ctx.globalAlpha = vA * lit;
          ctx.fillStyle = PANEL;
          ctx.strokeStyle = col;
          ctx.lineWidth = 1;
          rr(ctx, mx, my - mh / 2, mw, mh, 3);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = col;
          ctx.beginPath();
          ctx.arc(mx + 6, my - mh / 2 + 5, 1.8, 0, 6.28);
          ctx.fill();
        }
        // 視聴側 PC でも音声が届いていることを示すバー。
        ctx.globalAlpha = vA * lit;
        for (let lai = 0; lai < 5; lai++) {
          const lah = 3 + Math.abs(Math.sin(amb * 4 + lai * 0.9 + 0.4)) * 8;
          ctx.fillStyle = GREEN;
          rr(ctx, lapX - 14 + lai * 6, lapY + 32 - lah / 2, 3.5, lah, 1.75);
          ctx.fill();
        }
        ctx.globalAlpha = vA;
      }
      ctx.fillStyle = TXT;
      ctx.font = labelFont(11);
      ctx.fillText(tr("anim.viewerPc"), lapX, lapY + lh / 2 + 20);

      const pw = 52;
      const ph2 = 104;
      ctx.fillStyle = PANEL;
      ctx.strokeStyle = BORDER;
      rr(ctx, phX - pw / 2, phY - ph2 / 2, pw, ph2, 10);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = BG;
      rr(ctx, phX - pw / 2 + 4, phY - ph2 / 2 + 4, pw - 8, ph2 - 8, 7);
      ctx.fill();
      if (lit > 0) {
        ctx.globalAlpha = vA * lit;
        const sel = Math.floor(amb / 2.5) % 3;
        const col = winCols[sel];
        if (col) {
          ctx.fillStyle = PANEL;
          ctx.strokeStyle = col;
          ctx.lineWidth = 1;
          rr(ctx, phX - pw / 2 + 8, phY - 20, pw - 16, 30, 3);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = col;
          ctx.beginPath();
          ctx.arc(phX - pw / 2 + 13, phY - 15, 1.8, 0, 6.28);
          ctx.fill();
        }
        for (let pi2 = 0; pi2 < 5; pi2++) {
          const hh2 = 3 + Math.abs(Math.sin(amb * 4 + pi2)) * 8;
          ctx.fillStyle = GREEN;
          rr(ctx, phX - 14 + pi2 * 6, phY + 26 - hh2 / 2, 3.5, hh2, 1.75);
          ctx.fill();
        }
        ctx.globalAlpha = vA;
      }
      ctx.fillStyle = TXT;
      ctx.fillText(tr("anim.viewerMobile"), phX, phY + ph2 / 2 + 18);
      ctx.restore();
    }

    const noteA = eoc((t - 4.6) / 0.8);
    if (noteA > 0) {
      ctx.globalAlpha = noteA;
      ctx.fillStyle = TXT;
      ctx.font = labelFont(12);
      ctx.fillText(tr("anim.noInstalls"), LW * 0.5, LH - 18);
      ctx.globalAlpha = 1;
    }

    ctx.restore();
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}
